import { useState, useEffect, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { 
  Camera, Upload, Trophy, TrendingUp, Scale, Ruler, 
  Flame, Target, Calendar, ChevronLeft, ChevronRight, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PhotoCompare } from '../components/PhotoCompare';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine, AreaChart, Area 
} from 'recharts';
import { 
  getSettings, 
  getAllWeightLogs, 
  saveWeightLog,
  getAllMeasurementLogs,
  saveMeasurementLog,
  getAllProgressPhotos,
  saveProgressPhoto,
  deleteProgressPhoto,
  getAllWorkoutLogs,
  getAllSupplementLogs
} from '../lib/db';
import { getDateString, formatDate, generateDateRange } from '../lib/constants';
import { calculateProgress, calculateMovingAverage, compressImage } from '../lib/utils';

export const Progress = () => {
  const [settings, setSettings] = useState(null);
  const [weightData, setWeightData] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [newWeight, setNewWeight] = useState('');
  const [newMeasurements, setNewMeasurements] = useState({ waist: '', chest: '', arms: '', thighs: '' });
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const today = getDateString();

  const loadData = useCallback(async () => {
    try {
      const [settingsData, weights, measures, pics, workoutLogs, suppLogs] = await Promise.all([
        getSettings(),
        getAllWeightLogs(),
        getAllMeasurementLogs(),
        getAllProgressPhotos(),
        getAllWorkoutLogs(),
        getAllSupplementLogs(),
      ]);
      
      setSettings(settingsData);
      setWeightData(weights.sort((a, b) => new Date(a.date) - new Date(b.date)));
      setMeasurements(measures.sort((a, b) => new Date(b.date) - new Date(a.date)));
      setPhotos(pics.sort((a, b) => new Date(a.date) - new Date(b.date)));
      setWorkouts(workoutLogs);
      setSupplements(suppLogs);
    } catch (error) {
      console.error('Error loading progress data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const logWeight = async () => {
    const weight = parseFloat(newWeight);
    if (isNaN(weight) || weight < 30 || weight > 300) {
      toast.error('Enter a valid weight');
      return;
    }

    await saveWeightLog({ date: today, weight });
    setWeightData(prev => {
      const filtered = prev.filter(w => w.date !== today);
      return [...filtered, { date: today, weight }].sort((a, b) => new Date(a.date) - new Date(b.date));
    });
    setNewWeight('');
    toast.success('Weight logged!');
  };

  const logMeasurements = async () => {
    const measurement = {
      id: uuidv4(),
      date: today,
      waist: parseFloat(newMeasurements.waist) || 0,
      chest: parseFloat(newMeasurements.chest) || 0,
      arms: parseFloat(newMeasurements.arms) || 0,
      thighs: parseFloat(newMeasurements.thighs) || 0,
    };

    await saveMeasurementLog(measurement);
    setMeasurements(prev => [measurement, ...prev.filter(m => m.date !== today)]);
    setNewMeasurements({ waist: '', chest: '', arms: '', thighs: '' });
    toast.success('Measurements logged!');
  };

  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file);
      const photo = {
        id: uuidv4(),
        date: today,
        type, // 'front' or 'side'
        image: compressed,
        timestamp: new Date().toISOString(),
      };

      await saveProgressPhoto(photo);
      setPhotos(prev => [...prev, photo].sort((a, b) => new Date(a.date) - new Date(b.date)));
      toast.success('Photo saved!');
    } catch (error) {
      console.error('Error saving photo:', error);
      toast.error('Failed to save photo');
    }
  };

  const removePhoto = async (id) => {
    await deleteProgressPhoto(id);
    setPhotos(prev => prev.filter(p => p.id !== id));
    toast.success('Photo deleted');
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Calculate stats
  const currentWeight = weightData.length > 0 
    ? weightData[weightData.length - 1].weight 
    : settings.startWeight;
  const totalLost = settings.startWeight - currentWeight;
  const weeklyLoss = weightData.length >= 7 
    ? (weightData[weightData.length - 7]?.weight || settings.startWeight) - currentWeight 
    : 0;
  const avgWeeklyRate = weightData.length > 7 
    ? totalLost / (weightData.length / 7) 
    : totalLost;
  const completedWorkouts = workouts.filter(w => w.completed).length;
  
  const totalSuppsExpected = supplements.length > 0 
    ? Math.max(...supplements.map(s => {
        const date = new Date(s.date);
        const start = new Date(settings.startDate);
        return Math.ceil((date - start) / (1000 * 60 * 60 * 24));
      })) * 15 // ~15 supps per day
    : 1;
  const suppCompliance = Math.min(100, Math.round((supplements.length / totalSuppsExpected) * 100));

  // Prepare chart data with moving average
  const chartData = calculateMovingAverage(
    weightData.map(w => ({ date: w.date, weight: w.weight })),
    7
  );

  // Target line data
  const targetWeight = settings.targetWeight;

  // Get unique photo dates for comparison
  const photoDates = [...new Set(photos.map(p => p.date))];
  const beforePhotos = photos.filter(p => p.date === photoDates[0]);
  const afterPhotos = photos.filter(p => p.date === photoDates[photoDates.length - 1]);

  return (
    <div className="space-y-6" data-testid="progress-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black tracking-tight">Progress</h1>
        <p className="text-xs text-gray-500">Your transformation journey</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3" data-testid="stats-cards">
        <StatCard
          icon={<Scale className="text-[#00E676]" size={18} />}
          label="Total Lost"
          value={`${totalLost.toFixed(1)}kg`}
          highlight={totalLost > 0}
        />
        <StatCard
          icon={<TrendingUp className="text-blue-400" size={18} />}
          label="Avg Weekly"
          value={`${avgWeeklyRate.toFixed(1)}kg`}
        />
        <StatCard
          icon={<Flame className="text-orange-400" size={18} />}
          label="Workouts"
          value={completedWorkouts.toString()}
        />
        <StatCard
          icon={<Target className="text-purple-400" size={18} />}
          label="Supp Compliance"
          value={`${suppCompliance}%`}
        />
      </div>

      <Tabs defaultValue="weight" className="w-full">
        <TabsList className="grid grid-cols-4 bg-[#161625] border border-white/5 p-1 rounded-xl">
          <TabsTrigger value="weight" className="data-[state=active]:bg-[#24243e] rounded-lg text-xs">Weight</TabsTrigger>
          <TabsTrigger value="body" className="data-[state=active]:bg-[#24243e] rounded-lg text-xs">Body</TabsTrigger>
          <TabsTrigger value="photos" className="data-[state=active]:bg-[#24243e] rounded-lg text-xs">Photos</TabsTrigger>
          <TabsTrigger value="lifts" className="data-[state=active]:bg-[#24243e] rounded-lg text-xs">Lifts</TabsTrigger>
        </TabsList>

        {/* Weight Tab */}
        <TabsContent value="weight" className="space-y-4 mt-4">
          {/* Weight Input */}
          <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="weight-input">
            <div className="flex gap-3">
              <Input
                type="number"
                inputMode="decimal"
                placeholder="Today's weight (kg)"
                value={newWeight}
                onChange={(e) => setNewWeight(e.target.value)}
                className="flex-1 bg-[#161625] border-white/10 font-mono text-lg"
                data-testid="weight-input-field"
              />
              <Button 
                onClick={logWeight}
                className="bg-[#00E676] text-black font-bold hover:bg-[#00c853]"
                data-testid="log-weight-btn"
              >
                Log
              </Button>
            </div>
          </div>

          {/* Weight Chart */}
          <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="weight-chart">
            <h3 className="text-sm font-bold mb-4">Weight Over Time</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E676" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00E676" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2A2A40" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#6B7280', fontSize: 10 }}
                    tickFormatter={(d) => new Date(d).getDate()}
                  />
                  <YAxis 
                    domain={['dataMin - 2', 'dataMax + 2']}
                    tick={{ fill: '#6B7280', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#24243e', 
                      border: '1px solid #2A2A40',
                      borderRadius: '8px',
                    }}
                    labelFormatter={(d) => formatDate(d)}
                  />
                  <ReferenceLine 
                    y={targetWeight} 
                    stroke="#00E676" 
                    strokeDasharray="5 5"
                    label={{ value: 'Target', fill: '#00E676', fontSize: 10 }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="weight" 
                    stroke="#00E676" 
                    fill="url(#weightGradient)"
                    strokeWidth={2}
                  />
                  {chartData.some(d => d.movingAvg) && (
                    <Line 
                      type="monotone" 
                      dataKey="movingAvg" 
                      stroke="#FF6B6B" 
                      strokeWidth={2}
                      dot={false}
                      name="7-day Avg"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        {/* Body Measurements Tab */}
        <TabsContent value="body" className="space-y-4 mt-4">
          {/* Measurement Input */}
          <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="measurement-input">
            <h3 className="text-sm font-bold mb-3">Log Measurements (cm)</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <Input
                type="number"
                inputMode="decimal"
                placeholder="Waist"
                value={newMeasurements.waist}
                onChange={(e) => setNewMeasurements(prev => ({ ...prev, waist: e.target.value }))}
                className="bg-[#161625] border-white/10"
              />
              <Input
                type="number"
                inputMode="decimal"
                placeholder="Chest"
                value={newMeasurements.chest}
                onChange={(e) => setNewMeasurements(prev => ({ ...prev, chest: e.target.value }))}
                className="bg-[#161625] border-white/10"
              />
              <Input
                type="number"
                inputMode="decimal"
                placeholder="Arms"
                value={newMeasurements.arms}
                onChange={(e) => setNewMeasurements(prev => ({ ...prev, arms: e.target.value }))}
                className="bg-[#161625] border-white/10"
              />
              <Input
                type="number"
                inputMode="decimal"
                placeholder="Thighs"
                value={newMeasurements.thighs}
                onChange={(e) => setNewMeasurements(prev => ({ ...prev, thighs: e.target.value }))}
                className="bg-[#161625] border-white/10"
              />
            </div>
            <Button 
              onClick={logMeasurements}
              className="w-full bg-[#00E676] text-black font-bold hover:bg-[#00c853]"
              data-testid="log-measurements-btn"
            >
              Log Measurements
            </Button>
          </div>

          {/* Measurement History */}
          {measurements.length > 0 && (
            <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="measurement-history">
              <h3 className="text-sm font-bold mb-3">Recent Measurements</h3>
              <div className="space-y-2">
                {measurements.slice(0, 5).map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-[#161625] rounded-xl">
                    <span className="text-xs text-gray-500">{formatDate(m.date)}</span>
                    <div className="flex gap-4 text-xs font-mono">
                      <span>W: {m.waist}cm</span>
                      <span>C: {m.chest}cm</span>
                      <span>A: {m.arms}cm</span>
                      <span>T: {m.thighs}cm</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Photos Tab */}
        <TabsContent value="photos" className="space-y-4 mt-4">
          {/* Photo Upload */}
          <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="photo-upload">
            <h3 className="text-sm font-bold mb-3">Progress Photos</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handlePhotoUpload(e, 'front')}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => cameraInputRef.current?.click()}
                  className="w-full h-20 flex-col gap-1 border-dashed border-white/20"
                  data-testid="camera-btn"
                >
                  <Camera size={20} />
                  <span className="text-xs">Take Photo</span>
                </Button>
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePhotoUpload(e, 'front')}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-20 flex-col gap-1 border-dashed border-white/20"
                  data-testid="upload-btn"
                >
                  <Upload size={20} />
                  <span className="text-xs">Upload</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Photo Comparison */}
          {photos.length >= 2 && (
            <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="photo-compare">
              <h3 className="text-sm font-bold mb-3">Before / After</h3>
              <PhotoCompare 
                beforeImage={beforePhotos[0]?.image} 
                afterImage={afterPhotos[afterPhotos.length - 1]?.image} 
              />
            </div>
          )}

          {/* Photo Gallery */}
          {photos.length > 0 && (
            <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="photo-gallery">
              <h3 className="text-sm font-bold mb-3">Gallery</h3>
              <div className="grid grid-cols-3 gap-2">
                {photos.map(photo => (
                  <div key={photo.id} className="relative group aspect-square">
                    <img 
                      src={photo.image} 
                      alt={`Progress ${photo.date}`}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <Button
                      size="icon"
                      variant="destructive"
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-photo-${photo.id}`}
                    >
                      <Trash2 size={12} />
                    </Button>
                    <span className="absolute bottom-1 left-1 text-[8px] bg-black/60 px-1 rounded">
                      {formatDate(photo.date)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Lifts Tab */}
        <TabsContent value="lifts" className="space-y-4 mt-4">
          <LiftProgressSection workouts={workouts} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const StatCard = ({ icon, label, value, highlight }) => (
  <div className={`bg-[#24243e] border rounded-xl p-4 ${highlight ? 'border-[#00E676]/30' : 'border-white/5'}`}>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</span>
    </div>
    <p className={`text-xl font-black font-mono ${highlight ? 'text-[#00E676]' : ''}`}>{value}</p>
  </div>
);

const LiftProgressSection = ({ workouts }) => {
  const gymWorkouts = workouts.filter(w => w.type === 'gym' && w.completed);
  
  if (gymWorkouts.length === 0) {
    return (
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-8 text-center">
        <Trophy size={32} className="mx-auto mb-3 text-gray-500" />
        <p className="text-gray-500">Complete workouts to see lift progress</p>
      </div>
    );
  }

  // Calculate PRs for each exercise
  const exercisePRs = {};
  gymWorkouts.forEach(workout => {
    workout.exercises?.forEach(exercise => {
      const maxWeight = Math.max(...exercise.sets.filter(s => s.completed && s.weight).map(s => parseFloat(s.weight) || 0));
      if (!exercisePRs[exercise.id] || maxWeight > exercisePRs[exercise.id].weight) {
        exercisePRs[exercise.id] = { 
          weight: maxWeight, 
          name: exercise.name,
          date: workout.date 
        };
      }
    });
  });

  // Get exercise progression data
  const getExerciseData = (exerciseId) => {
    return gymWorkouts
      .filter(w => w.exercises?.some(e => e.id === exerciseId))
      .map(w => {
        const exercise = w.exercises.find(e => e.id === exerciseId);
        const maxWeight = Math.max(...exercise.sets.filter(s => s.completed && s.weight).map(s => parseFloat(s.weight) || 0));
        return { date: w.date, weight: maxWeight || 0 };
      });
  };

  return (
    <div className="space-y-4">
      {/* PRs */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="prs-section">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="text-yellow-400" size={18} />
          <h3 className="text-sm font-bold">Personal Records</h3>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(exercisePRs).slice(0, 6).map(([id, pr]) => (
            <div key={id} className="bg-[#161625] rounded-xl p-3">
              <p className="text-xs text-gray-500 truncate">{pr.name}</p>
              <p className="text-lg font-black font-mono text-[#00E676]">{pr.weight}kg</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sample Exercise Chart */}
      {Object.keys(exercisePRs).length > 0 && (
        <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4" data-testid="lift-chart">
          <h3 className="text-sm font-bold mb-3">Bench Press Progress</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={getExerciseData('bench-press')}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A40" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#6B7280', fontSize: 10 }}
                  tickFormatter={(d) => new Date(d).getDate()}
                />
                <YAxis tick={{ fill: '#6B7280', fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#24243e', 
                    border: '1px solid #2A2A40',
                    borderRadius: '8px',
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="weight" 
                  stroke="#00E676" 
                  strokeWidth={2}
                  dot={{ fill: '#00E676', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Total Volume */}
      <div className="bg-[#24243e] border border-white/5 rounded-2xl p-4">
        <h3 className="text-sm font-bold mb-2">Total Sessions</h3>
        <p className="text-3xl font-black font-mono text-[#00E676]">{gymWorkouts.length}</p>
      </div>
    </div>
  );
};

export default Progress;
